import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCameraRetro } from "@fortawesome/free-solid-svg-icons";
import { Alert, Snackbar } from "@mui/material";
import {
  allroles,
  Editrole,
  Edituser,
  getlevel,
} from "../funtionsBD/Mainservice";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";

import "./EditUser.css";
import { useNavigate } from "react-router-dom";
import { isSuperAdminUser } from "../../utils/brandAuth";
import { formatRoleLabel } from "../../utils/roleLabels";
import { FINAL_OBJECTIVE_CLASSMENTS } from "../../constants/finalObjective";
import {
  federationClubFromUser,
  isLuxembourgFederationClub,
} from "../../utils/clubCountryLanguage";
import {
  rankingSystemLabel,
  resolveRankingSystem,
} from "../../utils/nationalRanking";
import { useBrand } from "../../context/BrandContext";
import { FlttFormDialog } from "../newcomer/FlttFormDialog";

const STYLE_BY_ID = {
  1: "Offensive",
  2: "Defensive",
  3: "All-Round",
};

function normalizeGrip(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw.includes("pen")) return "penhold";
  if (raw.includes("shake")) return "shakehand";
  return raw || "";
}

function normalizeHand(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw.startsWith("l")) return "left";
  if (raw.startsWith("r")) return "right";
  return raw || "";
}

function hasPlayerRoleSelected(roles) {
  return (
    Array.isArray(roles) &&
    (roles.includes("r4") || roles.includes("r7") || roles.includes("r9"))
  );
}

export const EditUser = ({ data }) => {
  const [pictures, setPictures] = useState([]);
  const [imgvalue, setimgvalue] = useState();
  const [Role, setRole] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]); // Multiple roles
  const [levels, setLevels] = useState([]);
  const [success, setsuccess] = useState();
  const [error, seterror] = useState();
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [flttOpen, setFlttOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const id = data.user_id;
  const showPlayerProfile = hasPlayerRoleSelected(selectedRoles);
  const actingClub = federationClubFromUser(
    (() => {
      try {
        return JSON.parse(localStorage.getItem("user") || "{}");
      } catch {
        return {};
      }
    })(),
    brand
  );
  const rankingSystem = resolveRankingSystem(actingClub);
  const rankingLabel = rankingSystemLabel(rankingSystem, t);

  const validate = (values) => {
    const errors = {};
    
    // Optional validation - only validate if field has a value
    if (values.alias && values.alias.length > 100) {
      errors.alias = `${t("must")}`;
    }
    
    if (values.fname && values.fname.length > 100) {
      errors.fname = `${t("must")}`;
    }
    
    if (values.lname && values.lname.length > 100) {
      errors.lname = `${t("must")}`;
    }
    
    // Email is optional but must be valid if provided
    if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = t('invalidEmail') || 'Invalid email format';
    }

    return errors;
  };

  const closealert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  useEffect(() => {
    getrole();
    loadLevels();
  }, []);

  useEffect(() => {
    if (!data) {
      console.warn('⚠️ EditUser - No data provided');
      return;
    }
    
    console.log('🔍 EditUser - Received data:', data);
    console.log('🔍 EditUser - Data keys:', Object.keys(data));
    console.log('🔍 EditUser - all_roles:', data.all_roles);
    console.log('🔍 EditUser - role_id:', data.role_id);
    
    // Load user's current roles from backend
    let currentRoles = [];
    
    // Check if backend returns all_roles array (preferred)
    if (data.all_roles && Array.isArray(data.all_roles) && data.all_roles.length > 0) {
      currentRoles = data.all_roles;
      console.log('✅ EditUser - Using all_roles array:', currentRoles);
    } 
    // Fallback: Handle role_id - could be string or object
    else if (typeof data.role_id === 'string' && data.role_id) {
      currentRoles = [data.role_id];
      console.log('✅ EditUser - Using role_id string:', currentRoles);
    } else if (data.role_id && typeof data.role_id === 'object' && data.role_id.role_id) {
      currentRoles = [data.role_id.role_id];
      console.log('✅ EditUser - Using role_id object:', currentRoles);
    } else {
      console.warn('⚠️ EditUser - No roles found in data, using empty array');
      currentRoles = [];
    }
    
    console.log('🔍 EditUser - Final currentRoles to set:', currentRoles);

    // Guest Player (r7) is club-scoped: only show it for THIS club's guest
    // membership. Global all_roles may still include r7 from another club.
    if (data.is_guest === true) {
      if (!currentRoles.includes("r7")) currentRoles = [...currentRoles, "r7"];
      if (!currentRoles.includes("r4")) currentRoles = [...currentRoles, "r4"];
    } else if (data.is_guest === false) {
      currentRoles = currentRoles.filter((r) => r !== "r7");
    }

    setSelectedRoles(currentRoles);

    const levelName =
      data.player_level?.level_name ||
      data.player_level?.level_description ||
      data.custom_level ||
      "";
    const styleName =
      data.style?.style_name ||
      data.style?.translations?.[0]?.name ||
      STYLE_BY_ID[Number(data.style_id)] ||
      "";
    
    formik.setValues({
      alias: data.user_alias || '',
      fname: data.user_first_name || '',
      lname: data.user_last_name || '',
      email: data.contact_email || data.user_email || '',
      gender: data.user_sex || 'male',
      bday: data.user_date_birth ? moment(data.user_date_birth).format("YYYY-MM-DD") : '',
      height: data.user_height || '',
      weight: data.user_weight || '',
      img: data.user_image || '',
      grip: normalizeGrip(data.player_grip),
      hand: normalizeHand(data.player_hand),
      level: levelName,
      style: styleName,
      classement: data.fltt_classement || data.custom_level || '',
    });
  }, [data]);

  const getrole = async () => {
    try {
      const response = await allroles();
      console.log('🔍 EditUser - All roles loaded:', response.data.data);
      setRole(response.data.data);
    } catch (error) {
      console.error('❌ EditUser - Error loading roles:', error);
    }
  };

  const loadLevels = async () => {
    try {
      const response = await getlevel();
      if (response?.data?.data) {
        setLevels(response.data.data);
      }
    } catch (err) {
      console.error("❌ EditUser - Error loading levels:", err);
    }
  };
  
  const currentViewerIsSuperAdmin = (() => {
    try {
      return isSuperAdminUser(JSON.parse(localStorage.getItem("user") || "{}"));
    } catch {
      return false;
    }
  })();

  const handleRoleToggle = (roleId) => {
    if (roleId === "r9" && !currentViewerIsSuperAdmin) return;
    setSelectedRoles(prev => {
      if (prev.includes(roleId)) {
        if (roleId === "r7") {
          return prev.filter((r) => r !== "r7");
        }
        // Guest Player still needs Player to join group sessions
        if (roleId === "r4" && prev.includes("r7")) {
          return prev;
        }
        if (prev.length > 1) {
          return prev.filter(r => r !== roleId);
        }
        alert(t('userMustHaveAtLeastOneRole') || 'User must have at least one role');
        return prev;
      }
      const next = [...prev, roleId];
      if (roleId === "r7" && !next.includes("r4")) {
        next.push("r4");
      }
      return next;
    });
  };
  
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    setimgvalue(e.target.value);
    setPictures(file);
    formik.setFieldValue("img", URL.createObjectURL(file));
  };

  const handleadd = async (values) => {
    try {
      // Update user roles first (creates tbl_player when Player role is assigned).
      // Club admins cannot see the Moderator chip, so keep an existing one.
      const rolesToSave = [...selectedRoles];
      if (
        !currentViewerIsSuperAdmin &&
        Array.isArray(data?.all_roles) &&
        data.all_roles.map((id) => String(id).toLowerCase()).includes("r9") &&
        !rolesToSave.includes("r9")
      ) {
        rolesToSave.push("r9");
      }
      await Editrole(rolesToSave, id);
      
      // Only update user data if there are actual changes
      // (allows admin to just update roles without changing other fields)
      const response = await Edituser(pictures, values, id);

      if (response.data.status === "error") {
        throw response.data;
      } else {
        setsuccess(response.data.message || 'User updated successfully');
        setOpen(true);
        setimgvalue("");
        
        // Navigate back immediately after successful save
        setTimeout(() => {
          navigate("/UserManagement");
        }, 1000); // Reduced from 2000ms to 1000ms
      }
    } catch (error) {
      console.log(error);
      seterror(error.message || 'Failed to update user');
      setOpen2(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      alias: "",
      fname: "",
      lname: "",
      email: "",
      gender: "male",
      bday: "",
      img: "",
      height: "",
      weight: "",
      type: "",
      grip: "",
      hand: "",
      level: "",
      style: "",
      classement: "",
    },
    validate,
    onSubmit: (values) => {
      handleadd(values);
    },
  });

  const agelimit = moment(new Date()).format("YYYY-MM-DD");
  const isSuperAdmin = currentViewerIsSuperAdmin;

  const visibleRoles = Role.filter((role) => {
    if (role.role_name === "Super Admin" && !isSuperAdmin) return false;
    if (
      (role.role_name === "Moderator" || role.role_id === "r9") &&
      !isSuperAdmin &&
      !selectedRoles.includes(role.role_id) &&
      !selectedRoles.includes("r9")
    ) {
      return false;
    }
    return true;
  });

  const classementOptions = Array.from(
    new Set([
      ...FINAL_OBJECTIVE_CLASSMENTS,
      ...(formik.values.classement ? [formik.values.classement] : []),
    ])
  );

  const levelOptions = (() => {
    if (!levels.length) return [];
    const lu = levels.filter(
      (level) =>
        level.level_name &&
        /^(A[123]|B[123]|C[123]|D)$/i.test(String(level.level_name).trim())
    );
    return lu.length ? lu : levels;
  })();

  return (
    <form className="add-exercise edit-user-form" onSubmit={formik.handleSubmit}>
      <div className="edit-user-hero">
        <div className="edit-pic">
          {formik.values.img ? (
            <img
              src={formik.values.img}
              alt={formik.values.fname || t("edituser")}
            />
          ) : (
            <div className="profile_img_icon" aria-hidden="true">
              {formik.values.fname ? formik.values.fname.slice(0, 1) : "+"}
            </div>
          )}
          <label htmlFor="img" className="edit-img-label">
            <FontAwesomeIcon icon={faCameraRetro} className="edit-camera" />
            <span className="visually-hidden">{t("edituser")}</span>
          </label>
        </div>
      </div>
      <input
        className="input_addpicsvg"
        type="file"
        name="img"
        value={imgvalue}
        id="img"
        accept="image/*"
        onChange={handleFileInputChange}
      />

      <section className="edit-user-section" aria-labelledby="edit-user-roles-title">
        <div className="edit-user-section__head">
          <h3 id="edit-user-roles-title" className="edit-user-section__title">
            {t("userrole")}
          </h3>
          <span className="edit-user-section__count">
            {selectedRoles.length} {t("selected")}
          </span>
        </div>
        {Role.length === 0 ? (
          <div className="edit-user-empty">
            {t("loading") || "Loading roles..."}
          </div>
        ) : (
          <div className="edit-user-roles" role="group" aria-labelledby="edit-user-roles-title">
            {visibleRoles.map((role) => {
              const isActive = selectedRoles.includes(role.role_id);
              const isModerator =
                role.role_name === "Moderator" || role.role_id === "r9";
              const lockModerator = isModerator && !isSuperAdmin;
              return (
                <label
                  key={role.role_id}
                  className={`edit-user-role${isActive ? " is-active" : ""}${
                    isModerator ? " edit-user-role--moderator" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    disabled={lockModerator}
                    onChange={() => handleRoleToggle(role.role_id)}
                  />
                  <span className="edit-user-role__copy">
                    <span className="edit-user-role__name">
                      {formatRoleLabel(role.role_name || role.role_id)}
                    </span>
                    {isModerator ? (
                      <span className="edit-user-role__sub">
                        {t("moderatorRoleChipHint") ||
                          "Separate role. Includes admin, coach, and player permissions."}
                      </span>
                    ) : null}
                  </span>
                  {isActive && (
                    <span className="edit-user-role__badge">{t("active")}</span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        <p className="edit-user-hint">
          {t("selectrole")}
        </p>
        <p className="edit-user-hint">
          {t("guestPlayerRoleHint") ||
            "Guest Player is for someone who competes at another club and trains in your group sessions. Their home club is not changed. Guests cannot play this club's championship."}
        </p>
        {visibleRoles.some((role) => role.role_name === "Moderator" || role.role_id === "r9") ? (
          <p className="edit-user-hint">
            {t("moderatorRoleHint") ||
              "Moderator is its own role. It can run the club like an admin, coach sessions, and play. You do not also need to tick Admin, Coach, or Player unless that person really holds those roles."}
          </p>
        ) : null}
        {Array.isArray(data?.linked_children) && data.linked_children.length > 0 ? (
          <div className="edit-user-hint" style={{ marginTop: 8 }}>
            <strong>{t("linkedChildren") || "Linked children"}:</strong>{" "}
            {data.linked_children
              .map(
                (c) =>
                  [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                  c.alias ||
                  c.user_id
              )
              .join(", ")}
          </div>
        ) : null}
        {selectedRoles.length === 0 && (
          <p className="edit-user-hint edit-user-hint--warn">
            {t("userMustHaveAtLeastOneRole") || "No roles currently assigned to this user"}
          </p>
        )}
      </section>

      <section className="edit-user-section" aria-labelledby="edit-user-details-title">
        <h3 id="edit-user-details-title" className="edit-user-section__title">
          {t("edituser")}
        </h3>
      <div className="edit-user-fields">
        <div className="input-detail edit-user-field--full">
          <label htmlFor="alias">{t("alias")}</label>
          <input
            id="alias"
            placeholder={t("writealias")}
            type="text"
            value={formik.values.alias}
            name="alias"
            onChange={formik.handleChange}
            autoComplete="nickname"
          />
          {formik.errors.alias ? (
            <div className="validation_error">{formik.errors.alias}</div>
          ) : null}
        </div>

        <div className="input-detail">
          <label htmlFor="fname">{t("firstname")}</label>
          <input
            id="fname"
            placeholder={t("writename")}
            type="text"
            value={formik.values.fname}
            name="fname"
            onChange={formik.handleChange}
            autoComplete="given-name"
          />
          {formik.errors.fname ? (
            <div className="validation_error">{formik.errors.fname}</div>
          ) : null}
        </div>

        <div className="input-detail">
          <label htmlFor="lname">{t("lastname")}</label>
          <input
            id="lname"
            placeholder={t("writename")}
            type="text"
            value={formik.values.lname}
            name="lname"
            onChange={formik.handleChange}
            autoComplete="family-name"
          />
          {formik.errors.lname ? (
            <div className="validation_error">{formik.errors.lname}</div>
          ) : null}
        </div>

        <div className="input-detail edit-user-field--full">
          <label htmlFor="bday">{t("birthdate")}</label>
          <input
            id="bday"
            type="date"
            max={agelimit}
            value={formik.values.bday}
            name="bday"
            onChange={(e) => {
              formik.setFieldValue("bday", e.target.value);
            }}
          />
          {formik.errors.bday ? (
            <div className="validation_error">{formik.errors.bday}</div>
          ) : null}
        </div>

        <div className="edit-user-pair">
          <div className="input-detail">
            <label htmlFor="weight">
              {t("weight")} (kg)
            </label>
            <input
              id="weight"
              placeholder={t("enterweight")}
              type="number"
              inputMode="decimal"
              name="weight"
              value={formik.values.weight}
              onChange={formik.handleChange}
            />
            {formik.errors.weight ? (
              <div className="validation_error">{formik.errors.weight}</div>
            ) : null}
          </div>

          <div className="input-detail">
            <label htmlFor="height">
              {t("height")} (cm)
            </label>
            <input
              id="height"
              placeholder={t("enterheight")}
              type="number"
              inputMode="decimal"
              name="height"
              value={formik.values.height}
              onChange={formik.handleChange}
            />
            {formik.errors.height ? (
              <div className="validation_error">{formik.errors.height}</div>
            ) : null}
          </div>
        </div>

        <div className="input-detail edit-user-field--full">
          <span id="gender-label" className="edit-user-label">{t("gender")}</span>
          <div className="edit-user-gender" role="radiogroup" aria-labelledby="gender-label">
            <label
              className={`edit-user-gender__opt${formik.values.gender === "female" ? " is-active" : ""}`}
            >
              <input
                type="radio"
                name="gender"
                value="female"
                checked={formik.values.gender === "female"}
                onChange={formik.handleChange}
              />
              {t("female")}
            </label>
            <label
              className={`edit-user-gender__opt${formik.values.gender === "male" ? " is-active" : ""}`}
            >
              <input
                type="radio"
                name="gender"
                value="male"
                checked={formik.values.gender === "male"}
                onChange={formik.handleChange}
              />
              {t("male")}
            </label>
          </div>
        </div>

        <div className="input-detail edit-user-field--full">
          <label htmlFor="email">{t("mail")}</label>
          <input
            id="email"
            type="email"
            placeholder={t("enteremail")}
            value={formik.values.email}
            onChange={formik.handleChange}
            name="email"
            autoComplete="email"
            inputMode="email"
            readOnly={!!data?.email_is_parent_contact}
          />
          {data?.email_is_parent_contact ? (
            <div className="edit-user-hint">
              {t("childUsesParentEmail") ||
                "Contact email is the linked parent/tutor address until this player opens their own login."}
            </div>
          ) : null}
          {formik.errors.email ? (
            <div className="validation_error">{formik.errors.email}</div>
          ) : null}
        </div>
      </div>
      </section>

      {showPlayerProfile ? (
        <section
          className="edit-user-section"
          aria-labelledby="edit-user-player-title"
        >
          <div className="edit-user-section__head">
            <h3 id="edit-user-player-title" className="edit-user-section__title">
              {t("playingProfile") || "Playing Profile"}
            </h3>
          </div>
          <p className="edit-user-hint">
            {t("adminPlayerProfileHint") ||
              "Update this player's grip, hand, level, style, and FLTT classement."}
          </p>
          <div className="edit-user-fields">
            <div className="input-detail">
              <label htmlFor="grip">{t("racketGrip") || "Racket grip"}</label>
              <select
                id="grip"
                name="grip"
                value={formik.values.grip || ""}
                onChange={formik.handleChange}
              >
                <option value="">{t("selectGrip") || "Select grip"}</option>
                <option value="shakehand">{t("shakehand") || "Shakehand"}</option>
                <option value="penhold">{t("penhold") || "Penhold"}</option>
              </select>
            </div>

            <div className="input-detail">
              <label htmlFor="hand">{t("playingHand") || "Playing hand"}</label>
              <select
                id="hand"
                name="hand"
                value={formik.values.hand || ""}
                onChange={formik.handleChange}
              >
                <option value="">{t("selectHand") || "Select hand"}</option>
                <option value="right">{t("rightHand") || "Right"}</option>
                <option value="left">{t("leftHand") || "Left"}</option>
              </select>
            </div>

            <div className="input-detail">
              <label htmlFor="level">{t("playingLevel") || "Playing level"}</label>
              <select
                id="level"
                name="level"
                value={formik.values.level || ""}
                onChange={formik.handleChange}
              >
                <option value="">{t("selectLevel") || "Select level"}</option>
                {levelOptions.map((level) => {
                  const displayName =
                    level.level_name ||
                    level.level_description ||
                    `Level ${level.level_id}`;
                  return (
                    <option key={level.level_id} value={level.level_name}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="input-detail">
              <label htmlFor="classement">
                {rankingLabel
                  ? `${rankingLabel} · ${t("classement") || "Classement"}`
                  : t("classement") || "Classement"}
              </label>
              {isLuxembourgFederationClub(actingClub) ? (
              <select
                id="classement"
                name="classement"
                value={formik.values.classement || ""}
                onChange={formik.handleChange}
              >
                <option value="">
                  {t("selectLuxembourgClassement") || "Select classement"}
                </option>
                {classementOptions.map((cls) => (
                  <option key={cls} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
              ) : (
              <input
                id="classement"
                name="classement"
                type="text"
                value={formik.values.classement || ""}
                onChange={formik.handleChange}
                placeholder={
                  t("nationalClassementHint", {
                    system: rankingLabel,
                    defaultValue: `${rankingLabel} classement / rating`,
                  }) || `${rankingLabel} classement / rating`
                }
              />
              )}
              <p className="edit-user-hint">
                {t("nationalRankingClubScopedHint") ||
                  "Saved for this club's national system only. Other clubs keep their own classement and points."}
              </p>
            </div>

            {isLuxembourgFederationClub(actingClub) ? (
              <div className="input-detail edit-user-field--full">
                <p className="edit-user-hint">
                  Generate the FLTT affiliation form from this profile. Pingflo asks for any detail that is still missing, then prints the form to sign.
                </p>
                <button type="button" className="edit-user-submit" onClick={() => setFlttOpen(true)}>
                  Generate the FLTT form
                </button>
                <FlttFormDialog
                  open={flttOpen}
                  userId={id}
                  clubId={actingClub?.club_id}
                  onClose={() => setFlttOpen(false)}
                />
              </div>
            ) : null}

            <div className="input-detail edit-user-field--full">
              <label htmlFor="style">{t("playingStyle") || "Playing style"}</label>
              <select
                id="style"
                name="style"
                value={formik.values.style || ""}
                onChange={formik.handleChange}
              >
                <option value="">{t("selectStyle") || "Select style"}</option>
                <option value="Offensive">{t("offensive") || "Offensive"}</option>
                <option value="Defensive">{t("defensive") || "Defensive"}</option>
                <option value="All-Round">{t("allRound") || "All-Round"}</option>
              </select>
            </div>
          </div>
        </section>
      ) : null}

      <div className="edit-user-actions">
        <button className="edit-user-submit" type="submit">
          {t("submit")}
        </button>
      </div>
      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={closealert}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert variant="filled" severity="success">
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={open2}
        autoHideDuration={3000}
        onClose={() => setOpen2(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert variant="filled" severity="error">
          {error}
        </Alert>
      </Snackbar>
    </form>
  );
};
